import { AfterViewInit, Directive, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

@Directive({
  selector: '[libModalKeyboard]',
  standalone: true,
})
export class ModalKeyboardDirective implements AfterViewInit, OnChanges {
  private static readonly FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    'a[href]',
    'input:not([type="hidden"]):not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  @Input() public libModalKeyboard = true;
  @Input() public libModalKeyboardCanCancel = true;
  @Input() public libModalKeyboardCanSubmit = true;
  @Input() public libModalKeyboardAutofocus = true;
  @Input() public libModalKeyboardAutofocusSelector = '';

  @Output() public readonly libModalKeyboardCancel = new EventEmitter<void>();
  @Output() public readonly libModalKeyboardSubmit = new EventEmitter<void>();

  public constructor(private readonly hostRef: ElementRef<HTMLElement>) {}

  public ngAfterViewInit(): void {
    this.focusInitialTarget();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['libModalKeyboard']?.currentValue === true) {
      this.focusInitialTarget();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  public onEscape(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (
      keyboardEvent.defaultPrevented
      || !this.libModalKeyboard
      || !this.libModalKeyboardCanCancel
    ) {
      return;
    }

    keyboardEvent.preventDefault();
    keyboardEvent.stopPropagation();
    this.libModalKeyboardCancel.emit();
  }

  @HostListener('document:keydown.enter', ['$event'])
  public onEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (
      keyboardEvent.defaultPrevented
      || !this.libModalKeyboard
      || !this.libModalKeyboardCanSubmit
      || keyboardEvent.altKey
      || keyboardEvent.ctrlKey
      || keyboardEvent.metaKey
      || keyboardEvent.shiftKey
      || keyboardEvent.isComposing
    ) {
      return;
    }
    const target = keyboardEvent.target as HTMLElement | null;
    if (!target) return;

    if (target.closest('textarea,[contenteditable="true"],[data-modal-enter-ignore="true"]')) {
      return;
    }

    keyboardEvent.preventDefault();
    keyboardEvent.stopPropagation();
    this.libModalKeyboardSubmit.emit();
  }

  @HostListener('keydown.tab', ['$event'])
  public onTab(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (!this.libModalKeyboard || keyboardEvent.defaultPrevented) {
      return;
    }

    const focusable = this.getFocusableElements();
    if (focusable.length === 0) {
      return;
    }

    const activeElement = document.activeElement as HTMLElement | null;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!activeElement || !this.hostRef.nativeElement.contains(activeElement)) {
      keyboardEvent.preventDefault();
      first.focus();
      return;
    }

    if (keyboardEvent.shiftKey && activeElement === first) {
      keyboardEvent.preventDefault();
      last.focus();
      return;
    }

    if (!keyboardEvent.shiftKey && activeElement === last) {
      keyboardEvent.preventDefault();
      first.focus();
    }
  }

  private focusInitialTarget(): void {
    if (!this.libModalKeyboard || !this.libModalKeyboardAutofocus) {
      return;
    }

    queueMicrotask(() => {
      if (!this.libModalKeyboard) {
        return;
      }

      const host = this.hostRef.nativeElement;
      const active = document.activeElement as HTMLElement | null;
      if (active && host.contains(active)) {
        return;
      }

      const explicit = this.libModalKeyboardAutofocusSelector.trim();
      if (explicit) {
        const target = host.querySelector<HTMLElement>(explicit);
        if (target && this.isFocusable(target)) {
          target.focus();
          return;
        }
      }

      const semantic =
        host.querySelector<HTMLElement>('[data-modal-primary-focus="true"]')
        ?? host.querySelector<HTMLElement>('[autofocus]');
      if (semantic && this.isFocusable(semantic)) {
        semantic.focus();
        return;
      }

      const [first] = this.getFocusableElements();
      first?.focus();
    });
  }

  private getFocusableElements(): HTMLElement[] {
    const host = this.hostRef.nativeElement;
    const all = Array.from(host.querySelectorAll<HTMLElement>(ModalKeyboardDirective.FOCUSABLE_SELECTOR));
    return all.filter((element) => this.isFocusable(element));
  }

  private isFocusable(element: HTMLElement): boolean {
    if (element.hasAttribute('disabled') || element.getAttribute('aria-hidden') === 'true') {
      return false;
    }
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }
}
